import React from "react";
import { DialogLayout } from "../../../ui/layouts/DialogLayout";
import { Button } from "../../../ui/components/Button";
import { Checkbox } from "../../../ui/components/Checkbox";
import { FeatherArrowRight, FeatherInfo } from "@subframe/core";
import { getAccountNameLeaf } from "../../../utils";

export interface GLClassificationLineItem {
  index: number;
  description: string;
  oldAccountId: string;
  oldAccountName: string;
  newAccountId: string;
  newAccountName: string;
  confidence: number;
}

interface GLClassificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lineItems: GLClassificationLineItem[];
  onApply: (selectedLineItems: GLClassificationLineItem[]) => void;
  onSkip: () => void;
}

export const GLClassificationModal: React.FC<GLClassificationModalProps> = ({
  open,
  onOpenChange,
  lineItems,
  onApply,
  onSkip,
}) => {
  const [selectedLineItemIndexes, setSelectedLineItemIndexes] = React.useState<
    Set<number>
  >(() => new Set(lineItems.map((item) => item.index)));

  React.useEffect(() => {
    setSelectedLineItemIndexes(new Set(lineItems.map((item) => item.index)));
  }, [lineItems]);

  const selectedLineItems = React.useMemo(
    () => lineItems.filter((item) => selectedLineItemIndexes.has(item.index)),
    [lineItems, selectedLineItemIndexes],
  );
  const hasSelection = selectedLineItems.length > 0;

  const handleCheckboxChange = (lineItemIndex: number, checked: boolean) => {
    setSelectedLineItemIndexes((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(lineItemIndex);
      } else {
        next.delete(lineItemIndex);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(selectedLineItems);
  };
  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-144 flex-col items-end gap-6 bg-default-background px-8 py-6">
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Apply Previous GL Change?
          </span>
          <span className="text-body font-body text-subtext-color">
            You recently updated the GL for a similar document. Here's what
            changed:
          </span>
        </div>
        <div className="flex w-full flex-col items-start gap-3">
          {lineItems.map((item) => {
            const isChecked = selectedLineItemIndexes.has(item.index);
            return (
              <div
                key={item.index}
                className="flex w-full items-start gap-3 rounded-md border border-solid border-brand-primary bg-white px-6 py-4"
              >
                <Checkbox
                  label=""
                  checked={isChecked}
                  onCheckedChange={(checked: boolean) =>
                    handleCheckboxChange(item.index, checked)
                  }
                />
                <div className="flex flex-col items-start gap-3 grow">
                  <div className="flex w-full items-center gap-2">
                    <span className="text-caption font-caption text-subtext-color">
                      Impacted Line Item:
                    </span>
                    <span className="text-body font-body text-default-font">
                      {item.description}
                    </span>
                  </div>
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                  <div className="flex w-full items-center gap-4">
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Old GL
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {getAccountNameLeaf(item.oldAccountName)}
                      </span>
                    </div>
                    <FeatherArrowRight className="text-body font-body text-neutral-400" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        New GL
                      </span>
                      <span className="text-body-bold font-body-bold text-brand-600">
                        {getAccountNameLeaf(item.newAccountName)}
                      </span>
                    </div>
                  </div>
                  {/* <div className="flex w-full items-center justify-between">
                <span className="text-caption font-caption text-subtext-color">
                  Confidence
                </span>
                <span className="text-body font-body text-neutral-500">
                  {`${Math.round(item.confidence * 100)}%`}
                </span>
              </div> */}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex w-full items-start gap-2 rounded-md bg-brand-50 px-4 py-3">
          <FeatherInfo className="text-body font-body text-brand-600" />
          <span className="text-caption font-caption text-subtext-color">
            Applying this keeps your GL mapping consistent across invoices.
          </span>
        </div>
        <Button
          className="hidden"
          variant="neutral-tertiary"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
        >
          View Details
        </Button>
        <div className="flex items-center justify-end gap-2">
          <Button variant="neutral-secondary" onClick={onSkip}>
            Skip
          </Button>
          <Button disabled={!hasSelection} onClick={handleApply}>
            Apply Change
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};
